import { useState } from "react"

import { RadioButton, Text } from "@minuthq/meatball-ui-react"

import { SearchFilter } from "src/components/Filter/SearchFilter"
import { ParadiseDetailsSection } from "src/components/Paradise/ParadiseDetailsLayout"
import { ParadisePager } from "src/components/Paradise/ParadisePager"
import { ParadiseTable } from "src/components/Paradise/ParadiseTable"
import {
  TruncatedHomeId,
  TruncatedOrganizationId,
  TruncatedUserId,
} from "src/components/Paradise/ParadiseTruncatedIds"
import { ParadiseUserHomesFilters } from "src/components/Paradise/ParadiseUsers/ParadiseUserDetails/ParadiseUserHomes/ParadiseUserHomesFilters"
import {
  PillsWrapper,
  RadioWrapper,
} from "src/components/Paradise/sharedStyles"
import { parseParadiseHomeSortField } from "src/data/paradise/paradiseHomes/logic/paradiseHome"
import { useFetchParadiseHomes } from "src/data/paradise/paradiseHomes/queries/paradiseHomeQueries"
import { IParadiseHome } from "src/data/paradise/paradiseHomes/types/paradiseHomeQuerytypes"
import { Routes } from "src/router/routes"
import { useRouter } from "src/router/useRouter"
import { useSearchParams } from "src/router/useSearchParams"
import { ColumnPopoverWrapper } from "src/ui/GridTable/useTableColumns/tableColumnStyles"
import { useSortParam } from "src/ui/GridTable/useTableColumns/useSortParam"
import {
  TableColumn,
  useTableColumns,
} from "src/ui/GridTable/useTableColumns/useTableColumns"
import FilterIcon from "src/ui/icons/calibrating.svg"
import { MBadge } from "src/ui/MBadge/MBadge"
import { formatUtcDate } from "src/utils/l10n"

const LIMIT = 15

export function ParadiseUserHomes({ userId }: { userId: string }) {
  const { navigate } = useRouter()

  const [offset, setOffset] = useState(0)
  const { searchParams: filter, setSearchParams: setFilter } = useSearchParams({
    keys: [
      {
        key: "homes_showDeletedHomes",
        type: "boolean",
      },
      {
        key: "homes_name",
        type: "string",
      },
    ],
  })

  const { sortValue, setSortValue } = useSortParam()

  const fetchedHomes = useFetchParadiseHomes({
    filter: {
      user_id: userId,
      limit: LIMIT,
      offset,
      deleted: !!filter.homes_showDeletedHomes,
      name: filter.homes_name ?? undefined,
      sort: parseParadiseHomeSortField(sortValue?.id)
        ? sortValue?.id
        : undefined,
      sort_by: sortValue?.order,
    },
    options: {
      select: (data) => {
        return {
          homes: data.homes.slice(offset, offset + LIMIT),
          total_count: data.homes.length,
        }
      },
      keepPreviousData: true,
    },
  })

  const textVariant = "nano"

  const tableColumns: TableColumn<IParadiseHome>[] = [
    {
      value: "home_id",
      label: "Home id",
      columnWidth: "min-content",
      disableClickPropagation: true,
      renderCell: (home) => {
        return (
          <TruncatedHomeId
            homeId={home.home_id}
            to={Routes.ParadiseHome.location(home.home_id)}
            textVariant={textVariant}
          />
        )
      },
    },
    {
      value: "name",
      label: "Name",
      columnWidth: "min-content",
      renderCell: (home) => {
        return <Text variant={textVariant}>{home.name}</Text>
      },
      popoverContent: (
        <ColumnPopoverWrapper>
          <SearchFilter
            initialValue={filter.homes_name ?? undefined}
            placeholder="Search by name"
            onChange={(v) => setFilter("homes_name", v)}
          />
        </ColumnPopoverWrapper>
      ),
      popoverIcon: FilterIcon,
    },
    {
      value: "createdAt",
      label: "Created at",
      columnWidth: "min-content",
      enableSort: true,
      renderCell: (home) => {
        return (
          <Text variant={textVariant}>
            {formatUtcDate({ date: home.created_at })}
          </Text>
        )
      },
    },
    {
      value: "organization_id",
      label: "Organization id",
      columnWidth: "min-content",
      renderCell: (home) => {
        return (
          <TruncatedOrganizationId
            organizationId={home.owner.id}
            to={Routes.ParadiseOrganization.location(home.owner.id)}
            textVariant={textVariant}
          />
        )
      },
    },
    {
      value: "created_by",
      label: "Created by",
      columnWidth: "min-content",
      disableClickPropagation: true,
      renderCell: (home) => {
        return (
          <TruncatedUserId
            userId={home.created_by}
            to={Routes.ParadiseUser.location(home.created_by)}
            textVariant={textVariant}
          />
        )
      },
    },
    {
      value: "deleted",
      label: "Deleted",
      columnWidth: "min-content",
      renderCell: (home) => {
        return <Text variant={textVariant}>{home.deleted ? "Yes" : "No"}</Text>
      },
      popoverContent: (
        <ColumnPopoverWrapper>
          <RadioWrapper>
            <RadioButton
              name="deleted"
              value="false"
              label="Show only deleted homes"
              selected={filter.homes_showDeletedHomes === true}
              onSelected={() => setFilter("homes_showDeletedHomes", true)}
            />
          </RadioWrapper>
        </ColumnPopoverWrapper>
      ),
      popoverIcon: FilterIcon,
    },
    {
      value: "deletedAt",
      label: "Deleted at",
      enableSort: true,
      columnWidth: "min-content",
      hidden: !fetchedHomes.data?.homes.some((home) => home.deleted_at),
      renderCell: (home) => {
        return (
          <Text variant={textVariant}>
            {home.deleted_at ? formatUtcDate({ date: home.deleted_at }) : "-"}
          </Text>
        )
      },
    },
    {
      value: "timezone",
      label: "Timezone",
      columnWidth: "min-content",
      renderCell: (home) => {
        return <Text variant={textVariant}>{home.timezone}</Text>
      },
    },
    {
      value: "violations",
      label: "Violations",
      columnWidth: "min-content",
      renderCell: (home) => {
        return (
          <Text variant={textVariant}>
            <PillsWrapper $noWrap>
              {home.violations?.map((violation, i) => (
                <MBadge key={i} size="x-small">
                  {violation}
                </MBadge>
              ))}
            </PillsWrapper>
          </Text>
        )
      },
    },
  ]

  const { headerElements, rows, templateColumns } = useTableColumns({
    columns: tableColumns,
    data: fetchedHomes.data?.homes,
    sort: sortValue,
    onSortChange: setSortValue,
    options: {
      disableColumnHiding: true,
    },
  })

  const shouldShowFilters = Object.values(filter).some(
    (value) => value !== null
  )

  return (
    <div>
      <ParadiseDetailsSection
        title={"Homes"}
        totalCount={fetchedHomes.data?.total_count}
      />

      {shouldShowFilters && (
        <ParadiseUserHomesFilters filter={filter} setFilter={setFilter} />
      )}
      <ParadiseTable
        header={headerElements}
        rows={rows ?? []}
        templateColumns={templateColumns}
        onRowClick={(index) => {
          if (fetchedHomes.data?.homes) {
            navigate(
              Routes.ParadiseHome.location(
                // @ts-expect-error: noUncheckedIndexedAccess
                fetchedHomes.data.homes[index].home_id
              )
            )
          }
        }}
        error={{
          hasError: fetchedHomes.isError,
          title: fetchedHomes.error?.message,
        }}
      />
      <ParadisePager
        limit={LIMIT}
        offset={offset}
        setOffset={setOffset}
        totalCount={fetchedHomes.data?.total_count}
      />
    </div>
  )
}
